import { useMemo, useState } from 'react';

import { roundADOTQuantity } from '@atom/components/common/requests/adotQuantityUtilities';
import { ADOT_ADJUSTMENT_REASON_OPTIONS } from '@atom/components/common/requests/assetRequestConstants';
import { Progress, Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetBudget, AssetDetail } from '@atom/types/asset';
import { AssetRequestCreateInput } from '@atom/types/assetRequest';

import { getADOTAdjustedStockLevel } from './adotCreateUtilities';
import ADOTQuantityAdjustmentSubtext from './ADOTQuantityAdjustmentSubtext';

import classes from './adotCreate.module.css';

const { MenuItem } = Select;

const styles = {
  label: {
    color: colors.neutral.dim,
  },
};

type ADOTAdjustmentCreateProps = {
  budget: AssetBudget;
  createInputs: AssetRequestCreateInput;
  updateCreateInput: (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => void;
  asset: AssetDetail;
  // TODO: [AM-16707]: Temp code until BE solution is built
  totalRemaining: number;
  totalRemainingLoading: boolean;
};

const ADOTAdjustmentCreate = ({
  budget,
  asset,
  createInputs,
  updateCreateInput,
  // TODO: [AM-16707]: Temp code until BE solution is built
  totalRemaining,
  totalRemainingLoading,
}: ADOTAdjustmentCreateProps) => {
  const [quantityText, setQuantityText] = useState<string>(
    createInputs?.quantity?.toString() ?? '',
  );

  const quantityError = useMemo(
    () => getADOTAdjustedStockLevel(totalRemaining, createInputs?.quantity) < 0,
    [createInputs.quantity, totalRemaining, totalRemainingLoading],
  );

  const numberUpdate = (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => {
    const rounded = roundADOTQuantity(value);
    const updatedValue =
      Number.isNaN(rounded) || rounded === 0 ? null : rounded;

    setQuantityText(value);
    updateCreateInput(updatedValue, property);
  };

  return (
    <>
      <div className={classes.viewRow}>
        <div className={classes.rowTitle}>Inventory Name</div>
        <div>{asset?.name}</div>
      </div>
      <div className={classes.inputContainer}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={quantityText}
          label={`Quantity Adjusted (${budget?.unit})`}
          onChange={event => numberUpdate(event.target.value, 'quantity')}
          onBlur={() =>
            setQuantityText(createInputs?.quantity?.toString() ?? '')
          }
          helperText={
            totalRemainingLoading ? (
              <Progress size={10} />
            ) : (
              `${totalRemaining} ${budget?.unit} in stock`
            )
          }
          error={quantityError}
          data-cy="assetRequestAdjustmentQuantity"
        />
        <ADOTQuantityAdjustmentSubtext
          quantity={createInputs?.quantity}
          budget={budget}
          dataCy="assetRequestAdjustmentQuantitySubtext"
          totalRemaining={totalRemaining}
          totalRemainingLoading={totalRemainingLoading}
        />
      </div>
      <div className={classes.inputContainer}>
        <Select
          key="adjustmentReason"
          label="Adjustment Reason"
          InputLabelProps={{ style: { color: colors.neutral.dim } }}
          fullWidth
          onChange={event => updateCreateInput(event.target.value, 'reason')}
          value={createInputs.reason}
          data-cy="assetRequestAdjustmentReasonSelect"
        >
          {ADOT_ADJUSTMENT_REASON_OPTIONS.map((option: string) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className={classes.inputContainer}>
        <TextField
          multiline
          slotProps={{ inputLabel: { style: styles.label } }}
          value={createInputs?.comments}
          label="Comments"
          onChange={event => updateCreateInput(event.target.value, 'comments')}
          data-cy="assetRequestAdjustmentComment"
        />
      </div>
    </>
  );
};

export default ADOTAdjustmentCreate;
